import ad from 'src/common/constants/analytics/categoryActionLabel/ad';
import { reportingAdFormats } from 'src/common/constants/reporting/adDisplayFormats';
import { reportingAdSlotNames } from 'src/common/constants/reporting/adSlot';
import { adTypes } from 'src/common/constants/reporting/adTypes';
import { apiBeta } from '../../client/api';
import { mintSingleton } from '../../client/mint';
import { MINT_ERROR, MINT_INFO } from '../constants/ads/logging';
import { formatDimensions } from '../utils/reporting';
import { logCategoryActionLabel, logClientError, logClientInfo } from './logging';
import { displaySlotLoaded } from './mint';

export default function mintEventDispatcher(dispatch) {
  return () => {
    function onMintError(event) {
      const data = {
        message: MINT_ERROR,
        context: event,
      };
      dispatch(logClientError(data));
    }

    function onMintInfo(info) {
      const data = {
        message: MINT_INFO,
        context: { info },
      };
      dispatch(logClientInfo(data));
    }

    function onDisplayAdPaused(adSizeString) {
      dispatch(
        logCategoryActionLabel({
          category: ad.category,
          action: adSizeString,
          label: ad.labels.pause,
        }),
      );
    }

    function onDisplayAdResumed(adSizeString) {
      dispatch(
        logCategoryActionLabel({
          category: ad.category,
          action: adSizeString,
          label: ad.labels.resume,
        }),
      );
    }

    function onDisplayAdRequested({ isCompanionAd, slot = {} }) {
      const { formatDefinition = {}, requestId = '' } = slot;
      const { adProvider = '', adUnitId = '', dimensions = [] } = formatDefinition || {};
      const formattedDimensions = formatDimensions(dimensions, true);

      apiBeta.telemetry.reportAdsDisplayRequested({
        adSlot: reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adDisplayFormat:
          formattedDimensions.length === 1
            ? formattedDimensions[0]
            : reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED,
        adDisplayFormatsAccepted: formattedDimensions || [
          reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED,
        ],
        adRequestId: requestId,
        adNetworkName: adProvider,
        adUnitId,
        isCompanionAd,
        // TODO: we should emit this from Mint, but since we've never included it, it's always effectively been false
        adRequestHasAmazonKeywords: false,
      });
    }

    function onDisplayAdResponse({ isCompanionAd = false, slot = {} }) {
      const { formatDefinition = {}, requestId = '', creativeId } = slot;
      const { adProvider = '', adUnitId = '' } = formatDefinition || {};

      apiBeta.telemetry.reportAdsDisplayResponseReceived({
        adSlot: reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adDisplayFormat: reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED, // NOTE: The responseReceived event from Google does not expose the size of the ad
        adRequestId: requestId,
        adNetworkName: adProvider,
        adCreativeId: creativeId?.toString() || '',
        adUnitId,
        isCompanionAd,
      });
    }

    function onDisplayAdFailed({ debugDescription, errorCode, errorMessage, slot = {} }) {
      const { formatDefinition = {}, requestId = '' } = slot;
      const { adProvider = '', adUnitId = '' } = formatDefinition || {};

      apiBeta.telemetry.reportAdsDisplayRequestFailed({
        adSlot: reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adRequestId: requestId,
        adDisplayFormat: reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED,
        adNetworkName: adProvider,
        adUnitId,
        errorMessage,
        errorCode,
        debugDescription,
      });
    }

    function onDisplayAdLoaded({ isCompanionAd, isInstream, slot = {} }) {
      const {
        creativeId,
        currentDimensions = [],
        elementId,
        requestId = '',
        formatDefinition = {},
      } = slot;
      const { adProvider = '', adUnitId = '' } = formatDefinition || {};
      const formattedDimensions = formatDimensions(currentDimensions);

      dispatch(displaySlotLoaded(elementId));

      apiBeta.telemetry.reportAdsDisplayImpression({
        adSlot: isInstream
          ? reportingAdSlotNames.AD_SLOT_INSTREAM
          : reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adDisplayFormat: formattedDimensions || reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED,
        adCreativeId: creativeId?.toString() || '',
        adRequestId: requestId,
        adNetworkName: adProvider || '',
        adUnitId,
        isCompanionAd,
      });
    }

    function onDisplayAdViewable({ isCompanionAd, isInstream, isViewable, slot = {} }) {
      const { creativeId, currentDimensions = [], formatDefinition = {} } = slot;
      const formattedDimensions = formatDimensions(currentDimensions);

      apiBeta.telemetry.reportAdsDisplayViewabilityStatus({
        adSlot: isInstream
          ? reportingAdSlotNames.AD_SLOT_INSTREAM
          : reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adDisplayFormat: formattedDimensions || reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED,
        adCreativeId: creativeId?.toString() || '',
        adNetworkName: formatDefinition?.adProvider || '',
        isCompanionAd,
        isViewable,
      });
    }

    function onDisplayAdClicked({ isCompanionAd, isInstream, slot = {} }) {
      const { creativeId, currentDimensions = [], formatDefinition = {} } = slot;
      const formattedDimensions = formatDimensions(currentDimensions);

      apiBeta.telemetry.reportAdsDisplayClicked({
        adSlot: isInstream
          ? reportingAdSlotNames.AD_SLOT_INSTREAM
          : reportingAdSlotNames.AD_SLOT_DISPLAY,
        adType: adTypes.AD_TYPE_DISPLAY,
        adDisplayFormat: formattedDimensions,
        adCreativeId: creativeId?.toString() || '',
        adNetworkName: formatDefinition?.adProvider || '',
        destinationUrl: '',
        isCompanionAd,
      });
    }

    let mintEventsAndCallbacks;

    function generateEventsAndCallbacks() {
      const {
        Media: { display } = {},
        Logging: { info, error } = {},
        Status: { requested, response, failed, loaded, viewable, click, paused, resumed } = {},
      } = mintSingleton.instance?.Events || {};

      mintEventsAndCallbacks = [
        // Mint Events
        { events: [info], callback: onMintInfo },
        { events: [error], callback: onMintError },

        // Display Ad Events
        { events: [display, requested], callback: onDisplayAdRequested }, // AdsDisplayRequestedEvent
        { events: [display, response], callback: onDisplayAdResponse }, // AdsDisplayResponseReceivedEvent
        { events: [display, failed], callback: onDisplayAdFailed }, // AdsDisplayRequestFailedEvent
        { events: [display, loaded], callback: onDisplayAdLoaded }, // AdsDisplayImpressionEvent
        { events: [display, viewable], callback: onDisplayAdViewable }, // AdsDisplayViewabilityStatusEvent
        { events: [display, click], callback: onDisplayAdClicked }, // AdsDisplayClickedEvent
        { events: [display, paused], callback: onDisplayAdPaused },
        { events: [display, resumed], callback: onDisplayAdResumed },
      ];
    }

    function handleEventListenerRegistration(action) {
      mintEventsAndCallbacks.forEach(({ events, callback }) => {
        mintSingleton.instance?.event(...events)[action](callback);
      });
    }

    function addListeners() {
      handleEventListenerRegistration('listen');
    }

    function removeListeners() {
      handleEventListenerRegistration('remove');
    }

    async function init() {
      generateEventsAndCallbacks();
      addListeners();
    }

    init();
    return removeListeners;
  };
}
